/**
 * ============================================================================
 * Iron Meridian - Persistence Adapter
 * ============================================================================
 * 
 * PURPOSE:
 * Unified persistence layer abstracting SQLite (Cloudflare D1) storage.
 * Game engine uses this adapter without knowing where data lives.
 * 
 * FEATURES:
 * - Async API calls to D1 via Pages Functions
 * - Offline caching with flush-on-reconnect
 * - Migration from localStorage
 * - Pure data IO (no game logic)
 * - Connection status reporting
 * 
 * INTERFACE:
 * - initializeDatabase() → Initialize schema
 * - saveGameState() → Persist full game state
 * - loadGameState() → Load full game state
 * - saveTurnSnapshot() → Save turn for replay
 * - loadTurnHistory() → Load turn history for replay
 * - clearGame() → Delete game data
 * - saveSetting() / loadSetting() → User preferences
 * 
 * ============================================================================
 */

import { connectionStatus } from '../connectionStatus.js'

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE = '/api/db'
const CACHE_KEY = 'ironmeridian.persistence.cache'
const PENDING_WRITES_KEY = 'ironmeridian.persistence.pending'

// ============================================================================
// OFFLINE CACHE
// ============================================================================

/**
 * In-memory cache for offline support
 */
class OfflineCache {
  constructor() {
    this._cache = {}
    this._pendingWrites = []
    this._isOnline = true
    this._flushInProgress = false
    
    // Load pending writes from localStorage on init
    this._loadPendingWrites()
    
    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._handleOnline())
      window.addEventListener('offline', () => this._handleOffline())
    }
  }

  _loadPendingWrites() {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(PENDING_WRITES_KEY)
      if (stored) {
        this._pendingWrites = JSON.parse(stored)
      }
    } catch {
      this._pendingWrites = []
    }
  }

  _savePendingWrites() {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PENDING_WRITES_KEY, JSON.stringify(this._pendingWrites))
    } catch {
      // Ignore storage errors
    }
  }

  _handleOnline() {
    this._isOnline = true
    this.flush()
  }

  _handleOffline() {
    this._isOnline = false
  }

  get(key) {
    return this._cache[key]
  }

  set(key, value) {
    this._cache[key] = value
  }

  /**
   * Queue a write operation for later flush
   */
  queueWrite(operation) {
    this._pendingWrites.push({
      ...operation,
      timestamp: Date.now()
    })
    this._savePendingWrites()
  }

  /**
   * Flush pending writes to server
   */
  async flush() {
    if (this._flushInProgress || this._pendingWrites.length === 0) return
    if (!this._isOnline) return

    this._flushInProgress = true

    try {
      const writes = [...this._pendingWrites]
      
      for (const write of writes) {
        try {
          const response = await fetch(write.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(write.data)
          })

          if (response.ok) {
            // Remove successful write from queue
            const idx = this._pendingWrites.indexOf(write)
            if (idx > -1) {
              this._pendingWrites.splice(idx, 1)
            }
          }
        } catch {
          // Keep in queue for retry
        }
      }

      this._savePendingWrites()
    } finally {
      this._flushInProgress = false
    }
  }

  get pendingCount() {
    return this._pendingWrites.length
  }

  get isOnline() {
    return this._isOnline
  }
}

const cache = new OfflineCache()

// ============================================================================
// API HELPERS
// ============================================================================

/**
 * Make API call with offline fallback
 */
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    })

    if (!response.ok) {
      // Try to get the error message from the response body
      let errorMessage = `API error: ${response.status}`
      try {
        const errorBody = await response.json()
        if (errorBody.error) {
          errorMessage = errorBody.error
        }
      } catch {
        // Ignore JSON parse errors
      }
      console.error('[db.js] API error:', errorMessage, 'endpoint:', endpoint)
      throw new Error(errorMessage)
    }

    const result = await response.json()
    return result
  } catch (error) {
    // If offline, return cached data for reads
    if (!navigator.onLine && options.method !== 'POST') {
      const cacheKey = endpoint
      const cached = cache.get(cacheKey)
      if (cached) {
        return { success: true, data: cached, fromCache: true }
      }
    }
    throw error
  }
}

// ============================================================================
// DATABASE INITIALIZATION
// ============================================================================

/**
 * Initialize the database schema
 * @returns {Promise<{success: boolean, initialized: boolean, tables: string[]}>}
 */
export async function initializeDatabase() {
  try {
    const result = await apiCall('/init', { method: 'POST' })
    const success = result.data?.initialized ?? false
    
    // Report connection status
    connectionStatus.updateStatus({
      adapterType: 'sqlite',
      dbAvailable: success
    })
    
    return {
      success: true,
      initialized: success,
      tables: result.data?.tables ?? []
    }
  } catch (error) {
    console.warn('[Persistence] Database initialization failed:', error.message)
    
    // Report fallback to local
    connectionStatus.updateStatus({
      adapterType: 'local',
      dbAvailable: false
    })
    
    return {
      success: false,
      initialized: false,
      tables: [],
      error: error.message
    }
  }
}

/**
 * Check if database is available
 * @returns {Promise<{available: boolean, initialized: boolean}>}
 */
export async function checkDatabase() {
  try {
    const result = await apiCall('/init', { method: 'GET' })
    const available = result.data?.available ?? false
    
    // Report connection status
    connectionStatus.updateStatus({
      adapterType: 'sqlite',
      dbAvailable: available
    })
    
    return {
      available,
      initialized: result.data?.initialized ?? false
    }
  } catch {
    // Report fallback to local
    connectionStatus.updateStatus({
      adapterType: 'local',
      dbAvailable: false
    })
    
    return { available: false, initialized: false }
  }
}

// ============================================================================
// GAME STATE PERSISTENCE
// ============================================================================

/**
 * Save full game state to database
 * @param {Object} params - Game state parameters
 * @param {Object} params.gameState - Core game state
 * @param {Object} params.countries - Countries data
 * @param {Object} [params.diplomacy] - Diplomacy state
 * @param {Object} [params.techState] - Tech research state
 * @param {Array} [params.actionQueue] - Pending actions
 * @param {Array} [params.combatLog] - Recent combat events
 * @returns {Promise<{success: boolean, gameId?: string, savedAt?: string}>}
 */
export async function saveGameState({ 
  gameState, 
  countries, 
  diplomacy, 
  techState, 
  actionQueue, 
  combatLog 
}) {
  const payload = {
    gameState,
    countries,
    diplomacy,
    techState,
    actionQueue,
    combatLog
  }

  // Always cache locally
  cache.set('currentGame', payload)

  try {
    const result = await apiCall('/game/save', {
      method: 'POST',
      body: JSON.stringify(payload)
    })

    if (result.success) {
      // Report successful connection
      connectionStatus.reportRestored(true)
      
      return {
        success: true,
        gameId: result.data.gameId,
        savedAt: result.data.savedAt
      }
    }

    throw new Error(result.error || 'Save failed')
  } catch (error) {
    // Queue for later if offline
    if (!navigator.onLine) {
      cache.queueWrite({
        endpoint: `${API_BASE}/game/save`,
        data: payload
      })
      
      // Report offline status
      connectionStatus.reportError()
      
      return {
        success: true,
        gameId: gameState.gameId,
        savedAt: new Date().toISOString(),
        queued: true
      }
    }

    console.warn('[Persistence] Save failed:', error.message)
    
    // Report connection issue (but browser thinks we're online)
    connectionStatus.updateStatus({
      adapterType: 'local',
      dbAvailable: false
    })
    
    return { success: false, error: error.message }
  }
}

/**
 * Load game state from database
 * @param {string} [gameId] - Specific game ID (optional, loads active game if not specified)
 * @returns {Promise<{success: boolean, data?: Object}>}
 */
export async function loadGameState(gameId) {
  const endpoint = gameId ? `/game/load?id=${gameId}` : '/game/load'

  try {
    const result = await apiCall(endpoint)
    
    if (result.success && result.data) {
      // Update cache
      cache.set('currentGame', result.data)
      return { success: true, data: result.data }
    }

    // No saved game found
    return { success: true, data: null }
  } catch (error) {
    // Try cache on failure
    const cached = cache.get('currentGame')
    if (cached) {
      return { success: true, data: cached, fromCache: true }
    }

    return { success: false, error: error.message }
  }
}

/**
 * Check if there is a saved game in the database
 * @returns {Promise<{hasSave: boolean, gameId?: string, savedAt?: string}>}
 */
export async function hasSavedGame() {
  try {
    const result = await loadGameState()
    
    if (result.success && result.data?.gameState?.status === 'active') {
      return {
        hasSave: true,
        gameId: result.data.gameState.gameId,
        savedAt: result.data.savedAt || result.data.gameState.savedAt
      }
    }
    
    return { hasSave: false }
  } catch {
    return { hasSave: false }
  }
}

/**
 * Clear game data from database
 * @param {string} [gameId] - Specific game ID (clears active game if not specified)
 * @returns {Promise<{success: boolean}>}
 */
export async function clearGame(gameId) {
  cache.set('currentGame', null)

  // TODO: Implement DELETE endpoint
  // For now, we mark the game as abandoned
  try {
    const result = await apiCall('/game/save', {
      method: 'POST',
      body: JSON.stringify({
        gameState: { 
          gameId: gameId, 
          status: 'abandoned',
          currentTurn: 0,
          phase: 'ended',
          playerCountry: null
        },
        countries: {}
      })
    })

    return { success: result.success }
  } catch (error) {
    console.warn('[Persistence] Clear failed:', error.message)
    return { success: false, error: error.message }
  }
}

// ============================================================================
// TURN SNAPSHOTS (REPLAY)
// ============================================================================

/**
 * Save turn snapshot for replay
 * @param {Object} params - Turn snapshot parameters
 * @param {string} params.gameId - Game ID
 * @param {number} params.turnNumber - Turn number
 * @param {string} params.phase - Game phase
 * @param {Array} params.actions - Player actions
 * @param {Object} params.countries - Countries state
 * @param {Object} params.gameState - Game state
 * @param {Object} [params.results] - Turn resolution results
 * @returns {Promise<{success: boolean}>}
 */
export async function saveTurnSnapshot({ 
  gameId, 
  turnNumber, 
  phase, 
  actions, 
  countries, 
  gameState, 
  results 
}) {
  try {
    const result = await apiCall('/turn/commit', {
      method: 'POST',
      body: JSON.stringify({
        gameId,
        turnNumber,
        phase,
        actions,
        countries,
        gameState,
        results
      })
    })

    return { success: result.success }
  } catch (error) {
    // Queue for later if offline
    if (!navigator.onLine) {
      cache.queueWrite({
        endpoint: `${API_BASE}/turn/commit`,
        data: { gameId, turnNumber, phase, actions, countries, gameState, results }
      })
      return { success: true, queued: true }
    }

    console.warn('[Persistence] Turn snapshot failed:', error.message)
    return { success: false, error: error.message }
  }
}

/**
 * Load turn history for replay
 * @param {string} gameId - Game ID
 * @param {number} [turnNumber] - Specific turn (optional, loads all if not specified)
 * @returns {Promise<{success: boolean, data?: Object}>}
 */
export async function loadTurnHistory(gameId, turnNumber) {
  const params = new URLSearchParams({ gameId })
  if (turnNumber !== undefined) {
    params.set('turn', turnNumber.toString())
  }

  try {
    const result = await apiCall(`/replay/load?${params.toString()}`)
    return { success: result.success, data: result.data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ============================================================================
// SETTINGS PERSISTENCE
// ============================================================================

/**
 * Save a user setting
 * @param {string} key - Setting key
 * @param {*} value - Setting value
 * @returns {Promise<{success: boolean}>}
 */
export async function saveSetting(key, value) {
  // Always save to localStorage as fallback
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(`ironmeridian.setting.${key}`, JSON.stringify(value))
    } catch {
      // Ignore storage errors
    }
  }

  // TODO: Implement settings endpoint
  return { success: true }
}

/**
 * Load a user setting
 * @param {string} key - Setting key
 * @param {*} defaultValue - Default value if not found
 * @returns {Promise<{success: boolean, value: *}>}
 */
export async function loadSetting(key, defaultValue = null) {
  // Load from localStorage
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(`ironmeridian.setting.${key}`)
      if (stored) {
        return { success: true, value: JSON.parse(stored) }
      }
    } catch {
      // Fall through to default
    }
  }

  return { success: true, value: defaultValue }
}

// ============================================================================
// TUTORIAL STATE
// ============================================================================

/**
 * Check if tutorial is completed
 * @param {string} [tutorialKey='main'] - Tutorial identifier
 * @returns {Promise<boolean>}
 */
export async function isTutorialComplete(tutorialKey = 'main') {
  const result = await loadSetting(`tutorial.${tutorialKey}.complete`, false)
  return result.value === true
}

/**
 * Mark tutorial as completed
 * @param {string} [tutorialKey='main'] - Tutorial identifier
 * @returns {Promise<{success: boolean}>}
 */
export async function markTutorialComplete(tutorialKey = 'main') {
  return saveSetting(`tutorial.${tutorialKey}.complete`, true)
}

/**
 * Reset tutorial state
 * @param {string} [tutorialKey='main'] - Tutorial identifier
 * @returns {Promise<{success: boolean}>}
 */
export async function resetTutorial(tutorialKey = 'main') {
  return saveSetting(`tutorial.${tutorialKey}.complete`, false)
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get offline cache status
 * @returns {{isOnline: boolean, pendingWrites: number}}
 */
export function getCacheStatus() {
  return {
    isOnline: cache.isOnline,
    pendingWrites: cache.pendingCount
  }
}

/**
 * Flush pending writes manually
 * @returns {Promise<void>}
 */
export async function flushPendingWrites() {
  return cache.flush()
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const db = {
  // Initialization
  initializeDatabase,
  checkDatabase,
  
  // Game state
  saveGameState,
  loadGameState,
  clearGame,
  
  // Turn snapshots
  saveTurnSnapshot,
  loadTurnHistory,
  
  // Settings
  saveSetting,
  loadSetting,
  
  // Tutorial
  isTutorialComplete,
  markTutorialComplete,
  resetTutorial,
  
  // Utilities
  getCacheStatus,
  flushPendingWrites
}

export default db
